import React, { useEffect, useState } from "react";
import {
    FaArrowLeft, FaEye, FaCamera, FaClock, FaTrash,
    FaCommentDots, FaInfoCircle, FaCheckCircle
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { loadsResource, stopsResource, changeStatusStop } from "../../helpers/configRoutes/backend_helper";
import AddEvidenceModal from "./AddEvidenceModal";
import PreviewPDF from "../../components/modal/PreviewPDF";
import ConfirmDeleteModal from "../../components/modal/ConfirmDeleteModal";
import { GoogleMap, LoadScript, Marker, Circle } from "@react-google-maps/api";
import CustomButton from "../../components/form/CustomButton";

const StopDetails = () => {
    // Configuration & Styles
    const containerStyle = { width: "100%", height: "100%" };
    const mapOptions = {
        disableDefaultUI: true,
        zoomControl: false,
        styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] }
        ]
    };

    const navigate = useNavigate();
    const { id, load_id } = useParams();

    // State
    const [stop, setStop] = useState({});
    const [mapCenter, setMapCenter] = useState({ lat: 19.432608, lng: -99.133209 });
    const [modal, setModal] = useState({ show: false });
    const [userLocation, setUserLocation] = useState(null);
    const [heading, setHeading] = useState(0); // 👈 State for truck rotation
    const [isInsideGeofence, setIsInsideGeofence] = useState(false);
    const [geoCircle, setGeoCircle] = useState(null);
    const [reload, setReload] = useState(false);
    const [loading, setLoading] = useState(false);
    const [distanceToStop, setDistanceToStop] = useState(null);
    const [geofenceRadiusMiles, setGeofenceRadiusMiles] = useState(parseFloat(stop.geofence || 0.5));
    const [deleteModal, setDeleteModal] = useState({ show: false, loading: false });

    const hasActionAvailable = stop.can?.arrive || stop.can?.depart || stop.can?.complete;
    const canManageEvidences = Boolean(
        stop.can?.manage_evidences || [31, 32, 33].includes(Number(stop.status_operator_id))
    );
    const activeActionLabel = stop.can?.arrive
        ? "Arrival"
        : stop.can?.depart
            ? "Departure"
            : stop.can?.complete
                ? "Completion"
                : null;

    // Distance Calculation (Haversine)
    const haversineDistance = (coord1, coord2) => {
        const toRad = (x) => (x * Math.PI) / 180;
        const R = 6371000;
        const dLat = toRad(coord2.lat - coord1.lat);
        const dLon = toRad(coord2.lng - coord1.lng);
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(coord1.lat)) * Math.cos(toRad(coord2.lat)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    };

    const updateMapData = (data) => {
        const stopLat = parseFloat(data.lat);
        const stopLng = parseFloat(data.lng);
        const radiusMiles = parseFloat(data.geofence || 0.5);
        setMapCenter({ lat: stopLat, lng: stopLng });
        setGeofenceRadiusMiles(radiusMiles);

        // Watch position for real-time updates and heading
        navigator.geolocation.watchPosition((position) => {
            const { latitude, longitude, heading: currentHeading } = position.coords;

            setUserLocation({ lat: latitude, lng: longitude });

            // Set heading (default to 0 if null)
            if (currentHeading !== null) {
                setHeading(currentHeading);
            }

            const geofenceRadius = radiusMiles * 1609.34;
            const distance = haversineDistance({ lat: stopLat, lng: stopLng }, { lat: latitude, lng: longitude });
            const inside = distance <= geofenceRadius;

            setDistanceToStop(distance);
            setIsInsideGeofence(inside);
            setGeoCircle(
                <Circle
                    center={{ lat: stopLat, lng: stopLng }}
                    radius={geofenceRadius}
                    options={{
                        fillColor: inside ? "#10B981" : "#EF4444",
                        fillOpacity: 0.2,
                        strokeColor: inside ? "#10B981" : "#EF4444",
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                    }}
                />
            );
        }, (err) => console.error(err), { enableHighAccuracy: true });
    };

    useEffect(() => {
        const fetchData = async () => {
            const responseStop = await stopsResource("show", {}, id);
            setStop(responseStop.stop);
            if (responseStop.stop) updateMapData(responseStop.stop);
        };
        if (id) fetchData();
    }, [id, load_id, reload]);

    const changeStatus = async (status_id) => {
        if (!userLocation || !isInsideGeofence) {
            return;
        }

        setLoading(true);
        const response = await changeStatusStop({
            id: id,
            status_id: status_id,
            lat: userLocation.lat,
            lng: userLocation.lng,
        });
        if (response?.status) {
            const updatedLoadResponse = await loadsResource('show', {}, load_id);
            const updatedLoad = updatedLoadResponse?.load;
            const loadCompleted = updatedLoad?.status_id === 29
                || updatedLoad?.stops?.every((currentStop) => currentStop.status_operator_id === 33);

            setStop(response.stop);

            if (status_id === 33 && loadCompleted) {
                navigate('/loads', {
                    replace: true,
                    state: {
                        completedLoadId: Number(load_id),
                        refreshedAt: Date.now(),
                    },
                });
                setLoading(false);
                return;
            }

            setReload(!reload);
        }
        setLoading(false);
    };

    const deleteEvidence = async () => {
        if (!deleteModal?.evidenceId) {
            return;
        }

        setDeleteModal((prev) => ({ ...prev, loading: true }));

        const response = await stopsResource("put", {
            evidences_deleted: [deleteModal.evidenceId],
        }, id);

        if (response.status) {
            setDeleteModal({ show: false, loading: false });
            setReload(!reload);
            return;
        }

        setDeleteModal((prev) => ({ ...prev, loading: false }));
    };

    const distanceMiles = distanceToStop !== null ? distanceToStop / 1609.34 : null;
    const distanceLabel = distanceMiles !== null ? `${distanceMiles.toFixed(2)} mi away` : "Checking location...";
    const geofenceLabel = `${geofenceRadiusMiles.toFixed(2)} mi geofence`;
    const canBuildMarkerIcon =
        typeof window !== "undefined" &&
        typeof window.google?.maps?.Size === "function" &&
        typeof window.google?.maps?.Point === "function";
    const truckMarkerIcon = canBuildMarkerIcon
        ? {
            url: "/icons/truck.svg",
            scaledSize: new window.google.maps.Size(40, 40),
            anchor: new window.google.maps.Point(20, 20),
        }
        : undefined;

    return (
        <div className="min-h-screen bg-cs-bg-alt pb-safe font-sans text-cs-dark">
            {/* STICKY HEADER */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg px-4 py-4 border-b border-cs-border flex items-center justify-between">
                <button
                    onClick={() => navigate(`/active-load/${load_id}`)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-cs-bg-alt text-cs-muted active:scale-90 transition-all"
                >
                    <FaArrowLeft size={16} />
                </button>
                <div className="text-center">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-cs-muted font-bold">Stop Detail</p>
                    <h1 className="font-bold text-cs-dark text-sm truncate max-w-[150px]">
                        {stop.name || "Loading..."}
                    </h1>
                </div>
                <div className="bg-cs-primary-soft text-cs-primary text-[11px] font-black px-3 py-1 rounded-lg">
                    {stop.mile || 0} mi
                </div>
            </header>

            <div className="p-4 flex flex-col gap-6">

                {/* MAP CARD */}
                <div className="h-64 rounded-[2rem] overflow-hidden shadow-soft border-4 border-white relative bg-gray-200">
                    <LoadScript googleMapsApiKey="AIzaSyD2ihNBZrNGhWURi4BSk53T5lsgrJ08ciA">
                        <GoogleMap
                            mapContainerStyle={containerStyle}
                            center={mapCenter}
                            zoom={13}
                            options={mapOptions}
                        >
                            <Marker position={mapCenter} />
                            {geoCircle}
                            {userLocation && (
                                <Marker
                                    position={userLocation}
                                    icon={truckMarkerIcon}
                                />
                            )}
                        </GoogleMap>
                    </LoadScript>

                    {/* Floating Geofence Status */}
                    <div className={`absolute bottom-4 left-4 right-4 p-3 rounded-2xl backdrop-blur-md border flex items-center gap-3 transition-all ${isInsideGeofence ? 'bg-cs-success/90 border-white text-white' : 'bg-white/90 border-cs-border text-cs-muted'}`}>
                        <div className={`w-2.5 h-2.5 rounded-full ${isInsideGeofence ? 'bg-white animate-pulse' : 'bg-cs-danger'}`} />
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-wider">
                                {isInsideGeofence ? "Inside geofence" : "Outside geofence"}
                            </p>
                            <p className="text-[10px] opacity-90">
                                {distanceLabel} • {geofenceLabel}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-[2rem] shadow-soft p-5 border border-white">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-bold text-cs-muted uppercase tracking-[0.2em]">Workflow gate</p>
                            <p className="text-sm font-bold text-cs-dark mt-1">
                                {hasActionAvailable
                                    ? `${activeActionLabel} is the next action for this stop.`
                                    : "This stop is locked until the previous stop is completed."}
                            </p>
                            <p className="text-xs text-cs-muted mt-2 leading-relaxed">
                                {hasActionAvailable
                                    ? (isInsideGeofence
                                        ? "You are inside the allowed radius, so the action button is enabled below."
                                        : `Move inside the ${geofenceLabel} to enable the action button.`)
                                    : "Go back to the load itinerary and finish the highlighted next stop first."}
                            </p>
                        </div>
                        <div className={`shrink-0 px-3 py-1 rounded-full text-[10px] font-black uppercase ${hasActionAvailable ? 'bg-cs-primary-soft text-cs-primary' : 'bg-cs-bg-alt text-cs-muted border border-cs-border'}`}>
                            {hasActionAvailable ? 'Ready next' : 'Locked'}
                        </div>
                    </div>
                </div>

                {/* INFORMATION CARD */}
                <div className="bg-white rounded-[2rem] shadow-soft p-6 border border-white">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-8 h-8 rounded-xl bg-cs-primary-soft flex items-center justify-center text-cs-primary">
                            <FaInfoCircle size={14} />
                        </div>
                        <h2 className="text-xs font-bold text-cs-dark uppercase tracking-widest">General Information</h2>
                    </div>

                    <div className="space-y-4 text-sm">
                        <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-cs-muted uppercase tracking-tighter">Schedule</p>
                            <div className="flex items-center gap-2 font-bold">
                                <FaClock className="text-cs-primary" size={12} />
                                {stop.delivery_date || "TBD"}
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-cs-muted uppercase tracking-tighter">Status</p>
                            <span className="px-2.5 py-1 bg-cs-bg-alt rounded-lg text-[10px] font-black text-cs-primary uppercase border border-cs-primary/10">
                                {stop.statusOperator?.name || "Pending"}
                            </span>
                        </div>

                        {stop.comments?.length > 0 && (
                            <div className="pt-4 border-t border-cs-bg-alt">
                                <p className="text-[10px] font-bold text-cs-muted uppercase mb-2">Instructions</p>
                                <div className="p-3 bg-cs-bg-alt rounded-2xl flex gap-3">
                                    <FaCommentDots className="text-cs-primary shrink-0" size={14} />
                                    <p className="text-xs italic leading-relaxed">
                                        {stop.comments.map(c => c.comment).join(". ")}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* EVIDENCE SECTION */}
                <section>
                    <div className="flex items-center justify-between mb-4 px-2">
                        <h3 className="text-[11px] font-bold text-cs-muted uppercase tracking-[0.2em]">Evidences</h3>
                        {canManageEvidences && (
                            <button
                                onClick={() => setModal({ show: true, id: id, type: 1 })}
                                className="bg-cs-primary text-white px-4 py-2 rounded-xl text-[10px] font-bold shadow-lg active:scale-95 flex items-center gap-2"
                            >
                                <FaCamera /> ADD NEW
                            </button>
                        )}
                    </div>

                    <div className="bg-white rounded-[2rem] shadow-soft p-3 flex flex-col gap-2 border border-white">
                        {!canManageEvidences && (
                            <div className="px-3 pt-2">
                                <p className="text-[11px] text-cs-muted">
                                    Evidences unlock after the stop is marked as arrived.
                                </p>
                            </div>
                        )}
                        {stop.evidences?.length > 0 ? (
                            stop.evidences.map((evidence, index) => (
                                <div key={index} className="flex items-center justify-between p-3 rounded-2xl bg-cs-bg-alt/50">
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-cs-primary">
                                            <FaCamera size={16} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold truncate max-w-[150px]">{evidence.name}</p>
                                            <p className="text-[10px] text-cs-muted font-medium uppercase">{evidence.date}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {canManageEvidences && (
                                            <button
                                                onClick={() => setDeleteModal({
                                                    show: true,
                                                    loading: false,
                                                    evidenceId: evidence.id,
                                                    text: `Delete evidence "${evidence.name}"?`,
                                                    textButton: "Delete",
                                                    textLoad: "Deleting...",
                                                })}
                                                className="w-10 h-10 rounded-xl bg-cs-danger text-white flex items-center justify-center active:scale-90"
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setModal({ show: true, type: 2, url: evidence.url, title: evidence.name })}
                                            className="w-10 h-10 rounded-xl bg-cs-primary text-white flex items-center justify-center active:scale-90"
                                        >
                                            <FaEye />
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-10 text-center border-2 border-dashed border-cs-border rounded-[1.5rem]">
                                <p className="text-xs text-cs-muted italic">No evidences attached.</p>
                            </div>
                        )}
                    </div>
                </section>

                {/* ACTION FOOTER */}
                <div className="grid grid-cols-1 gap-3 pt-4 pb-12">
                    {!hasActionAvailable && (
                        <div className="rounded-[1.5rem] border border-cs-border bg-white px-4 py-4 text-center shadow-soft">
                            <p className="text-sm font-bold text-cs-dark">Stop locked</p>
                            <p className="text-xs text-cs-muted mt-2">
                                This stop cannot be updated yet. Complete the highlighted next stop from the load itinerary first.
                            </p>
                        </div>
                    )}

                    {stop.can?.arrive && (
                        <CustomButton
                            disabled={!isInsideGeofence || loading}
                            loading={loading}
                            onClick={() => changeStatus(31)}
                            className={`w-full py-4 rounded-2xl font-extrabold text-sm tracking-widest shadow-lg transition-all active:scale-[0.98] 
                                ${isInsideGeofence ? 'bg-cs-primary text-white shadow-cs-primary/30' : 'bg-gray-300 text-gray-500 opacity-60 cursor-not-allowed'}`}
                        >
                            {isInsideGeofence ? "CONFIRM ARRIVAL" : `MOVE INSIDE ${geofenceRadiusMiles.toFixed(2)} MI GEOFENCE`}
                        </CustomButton>
                    )}

                    {stop.can?.depart && (
                        <CustomButton
                            disabled={!isInsideGeofence || loading}
                            loading={loading}
                            onClick={() => changeStatus(32)}
                            className={`w-full py-4 rounded-2xl font-extrabold text-sm tracking-widest shadow-lg active:scale-[0.98] ${isInsideGeofence ? 'bg-cs-success text-white' : 'bg-gray-300 text-gray-500 opacity-60 cursor-not-allowed'}`}
                        >
                            <FaCheckCircle className="inline mr-2" /> {isInsideGeofence ? 'CONFIRM DEPARTURE' : `MOVE INSIDE ${geofenceRadiusMiles.toFixed(2)} MI GEOFENCE`}
                        </CustomButton>
                    )}

                    {stop.can?.complete && (
                        <CustomButton
                            disabled={!isInsideGeofence || loading}
                            loading={loading}
                            onClick={() => changeStatus(33)}
                            className={`w-full py-4 rounded-2xl font-extrabold text-sm tracking-widest shadow-lg active:scale-[0.98] ${isInsideGeofence ? 'bg-cs-dark text-white' : 'bg-gray-300 text-gray-500 opacity-60 cursor-not-allowed'}`}
                        >
                            {isInsideGeofence ? 'MARK AS COMPLETED' : `MOVE INSIDE ${geofenceRadiusMiles.toFixed(2)} MI GEOFENCE`}
                        </CustomButton>
                    )}
                </div>
            </div>

            {/* MODALS */}
            {modal.show && modal.type === 1 && (
                <AddEvidenceModal modal={modal} setModal={setModal} reload={reload} setReload={setReload} />
            )}
            {modal.show && modal.type === 2 && (
                <PreviewPDF modal={modal} setModal={setModal} />
            )}
            {deleteModal.show && (
                <ConfirmDeleteModal
                    action={deleteEvidence}
                    modal={deleteModal}
                    setModal={setDeleteModal}
                />
            )}
        </div>
    );
};

export default StopDetails;